'use client';

import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslations } from 'next-intl';
import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { useCoinBalances } from '@/lib/api/coin-balances/hooks';
import { scheduleRewardsCountersRefresh } from '@/lib/api/rewards-hub/refresh';
import type { TransactionsResult } from '@/lib/api/transaction/client';
import { BLOCKCHAINS_QK, TRANSACTIONS_QK } from '@/lib/api/transaction/hooks';
import type { Blockchain, Coin, Transaction } from '@/lib/api/transaction/models';
import { USER_BALANCES_QK } from '@/lib/api/user-balances/hooks';
import type { UserBalancesResult } from '@/lib/api/user-balances/models';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useDebouncedToast } from '@/lib/hooks/useDebouncedToast';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import { useBalance } from '@/lib/providers/BalanceProvider';
import { useCoinsBalance } from '@/lib/providers/CoinsBalanceProvider';
import { useCoins } from '@/lib/providers/CoinsProvider';
import { formatMoney } from '@/lib/utils/formatAmount';

type WalletSocketProviderProps = {
  children: ReactNode;
};

/** Ledger movements a trade produces (prediction market + futures). */
const TRADING_TX_TYPES = new Set([
  'TRADING_IN',
  'TRADING_OUT',
  'TRADING_FEE',
  'FUT_TRADING_IN',
  'FUT_TRADING_OUT',
  'FUT_TRADING_FEE',
]);

/**
 * How long a trading ledger entry keeps the generic `balanceUpdated` toast quiet.
 * The `BALANCE_UPDATE` frame carries no cause, so the transaction frame from the
 * same burst is what identifies the change as trade-driven.
 */
const TRADING_BALANCE_MUTE_MS = 1500;

// Balance WebSocket message types
interface BalanceUpdateDto {
  u: string; // userId (UUID)
  a: string | null; // assetId (UUID, can be null) - coin/asset id
  b: string | number; // balance (BigDecimal) - total balance
  f?: string | number; // frozenBalance (BigDecimal, optional) - frozen balance
  aop?: string | number; // averageOpenPrice (BigDecimal, optional) - average open price
  c?: string | number; // cost (BigDecimal, optional) - cost
}

// Success message DTO - contains list of balance items
interface SuccessBalanceItem {
  i: string; // id (UUID) - coin/asset id
  b: string | number; // balance (BigDecimal) - total balance
  f: string | number; // frozen (BigDecimal) - frozen balance
}

interface SuccessMessageDto {
  l: SuccessBalanceItem[]; // list of balance items
}

interface BalanceWebSocketMessage {
  t: string; // type
  d?: BalanceUpdateDto | SuccessMessageDto; // data
  r?: string; // error
}

// Transaction WebSocket message types
interface TransactionUpdateDto {
  i: string; // id (UUID)
  u: string; // userId (UUID)
  a: string; // assetId (UUID)
  t: string; // transactionType
  am: string | number; // amount (BigDecimal)
  s: string; // status
  cat: string; // createdAt (ISO-8601)
  e?: string; // externalTransactionId (optional)
  f?: string | number; // fees (BigDecimal, optional)
  on?: string; // outcomeName (optional)
  tn?: string; // tokenName (optional)
  ip?: boolean; // isPositive (optional)
  es?: string; // eventSlug (optional)
  ta?: string; // toAddress (optional)
  oi?: string; // orderId (optional, matches "o" from initiate response)
  er?: string; // error code (optional, e.g. ERROR_BALANCE, ERROR_HOT_WALLET)
  bu?: string; // blockExplorerUrl (optional)
  bn?: string; // blockchainName (optional)
}

interface HotWalletStatusUpdateDto {
  hwi: string; // hotWalletId
  ci: string; // coinId
  bi: string; // blockchainId
  ws: number; // status (1=normal, 2=congested, 3=maintenance)
}

interface TransactionWebSocketMessage {
  t: string; // type
  d?: TransactionUpdateDto | SuccessMessageDto | HotWalletStatusUpdateDto; // data
  r?: string; // error
}

// Pending balance update for user-balances cache
interface PendingUserBalanceUpdate {
  assetId: string;
  totalVolume: number; // b from WebSocket
  value: number; // b - f (available shares)
  averageOpenPrice?: number; // aop from WebSocket (optional)
  cost?: number; // c from WebSocket (optional)
  timestamp: number;
}

// Subscription context for components that need to react to real-time transaction updates
type TransactionUpdateListener = (transaction: Transaction) => void;

interface TransactionUpdateContextValue {
  subscribe: (listener: TransactionUpdateListener) => () => void;
}

const TransactionUpdateContext = createContext<TransactionUpdateContextValue>({
  subscribe: () => () => {},
});

export const useTransactionUpdates = () => useContext(TransactionUpdateContext);

// Pub/sub for BALANCE_UPDATE — allows components to react to real-time balance changes
export interface BalanceUpdatePayload {
  assetId: string;
  b: number;
  f: number;
  aop: number | null;
  c: number | null;
}

type BalanceUpdateListener = (payload: BalanceUpdatePayload) => void;

interface BalanceUpdateContextValue {
  subscribe: (listener: BalanceUpdateListener) => () => void;
}

const BalanceUpdateContext = createContext<BalanceUpdateContextValue>({
  subscribe: () => () => {},
});

export const useBalanceUpdates = () => useContext(BalanceUpdateContext);

export function WalletSocketProvider({ children }: WalletSocketProviderProps) {
  const { isAuthenticated, isLoading: isAuthLoading, clearAuthData } = useAuthContext();
  const { setBalance } = useBalance();
  const { setCoinBalance, getCoinBalance, clearBalances } = useCoinsBalance();
  const { coins } = useCoins();
  const authErrorHandledRef = useRef(false);
  // Tracks whether we've actually been authenticated, so the cache wipe below
  // fires only on a real logout — never on the initial anonymous/loading pass.
  const wasAuthenticatedRef = useRef(false);
  const queryClient = useQueryClient();
  const tToasts = useTranslations('toasts.wallet');
  const tCommon = useTranslations('common');

  // Initial REST snapshot of coin balances. WebSocket keeps the data live
  // afterwards; this only fills the store before the socket finishes its
  // handshake (and re-fills it on reconnect via refetchOnReconnect).
  const { data: restCoinBalances } = useCoinBalances();

  useEffect(() => {
    if (!restCoinBalances || restCoinBalances.length === 0) return;
    restCoinBalances.forEach(({ coinId, balance, frozen }) => {
      setCoinBalance(coinId, balance, frozen);
    });
    // Mirror USDC balance into legacy BalanceProvider so consumers that still
    // read useBalance() see a populated value before the socket connects.
    const usdcCoin = coins.find((c) => c.shortName.toUpperCase() === 'USDC');
    if (usdcCoin) {
      const usdcEntry = restCoinBalances.find((b) => b.coinId === usdcCoin.id);
      setBalance(usdcEntry?.balance ?? 0);
    }
  }, [restCoinBalances, coins, setCoinBalance, setBalance]);

  // Debounced toast for balance and transaction updates
  const debouncedToast = useDebouncedToast(500);
  // When a trade last moved the ledger — see TRADING_BALANCE_MUTE_MS.
  const lastTradingTxAtRef = useRef(0);

  // Pub/sub for transaction updates — allows components to react to WS transactions
  const txListenersRef = useRef<Set<TransactionUpdateListener>>(new Set());

  const subscribeTxUpdates = useCallback((listener: TransactionUpdateListener) => {
    txListenersRef.current.add(listener);
    return () => {
      txListenersRef.current.delete(listener);
    };
  }, []);

  const notifyTxListeners = useCallback((tx: Transaction) => {
    txListenersRef.current.forEach((listener) => listener(tx));
  }, []);

  // Pub/sub for balance updates — allows components to react to WS balance changes
  const balanceListenersRef = useRef<Set<BalanceUpdateListener>>(new Set());

  const subscribeBalanceUpdates = useCallback((listener: BalanceUpdateListener) => {
    balanceListenersRef.current.add(listener);
    return () => {
      balanceListenersRef.current.delete(listener);
    };
  }, []);

  const notifyBalanceListeners = useCallback((payload: BalanceUpdatePayload) => {
    balanceListenersRef.current.forEach((listener) => listener(payload));
  }, []);

  // Track pending user-balance updates by assetId (keep only latest per assetId)
  const pendingUserBalanceUpdatesRef = useRef<Map<string, PendingUserBalanceUpdate>>(new Map());
  const debounceTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update user-balances cache with pending updates
  const updateUserBalancesCache = useCallback(() => {
    const updates = Array.from(pendingUserBalanceUpdatesRef.current.values());
    if (updates.length === 0) return;

    // Clear pending updates
    pendingUserBalanceUpdatesRef.current.clear();

    // Find all active user-balances queries
    const allQueries = queryClient.getQueryCache().findAll({
      queryKey: [USER_BALANCES_QK],
    });

    updates.forEach((update) => {
      allQueries.forEach((query) => {
        queryClient.setQueryData<UserBalancesResult>(query.queryKey, (oldData) => {
          if (!oldData) {
            // If no data exists, create new entry
            return {
              balances: [
                {
                  id: update.assetId, // Use assetId as id (will be replaced by backend data on mount)
                  userId: null,
                  eventTokenId: update.assetId,
                  value: update.value,
                  totalVolume: update.totalVolume,
                  ...(update.averageOpenPrice !== undefined && {
                    averageOpenPrice: update.averageOpenPrice,
                  }),
                  ...(update.cost !== undefined && { cost: update.cost }),
                },
              ],
              page: 0,
              size: 1,
              totalPages: 1,
            };
          }

          // Find existing balance for this assetId
          // Try to find by eventTokenId first, then by id as fallback
          const existingIndex = oldData.balances.findIndex(
            (balance) => balance.eventTokenId === update.assetId || balance.id === update.assetId,
          );

          if (existingIndex >= 0) {
            // Update existing balance
            const updatedBalances = [...oldData.balances];
            const existingBalance = updatedBalances[existingIndex];
            // Preserve existing totalVolume only if shares are currently frozen
            // (i.e. TP/SL or limit sell orders exist). A WS message with b=0 in
            // that case is a spurious mid-flight value, not a real close. After a
            // genuine full sell there's nothing frozen, so we let totalVolume drop
            // to 0 and the position disappears.
            const existingTotalVolumeOrValue =
              existingBalance.totalVolume ?? existingBalance.value ?? 0;
            const existingFrozen = existingTotalVolumeOrValue - (existingBalance.value ?? 0);
            const shouldPreserveTotalVolume = update.totalVolume === 0 && existingFrozen > 0;
            const newTotalVolume = shouldPreserveTotalVolume
              ? existingTotalVolumeOrValue
              : update.totalVolume;
            updatedBalances[existingIndex] = {
              ...existingBalance,
              value: update.value, // available = b - f
              totalVolume: newTotalVolume, // b from WebSocket (protected from becoming 0)
              ...(update.averageOpenPrice !== undefined && {
                averageOpenPrice: update.averageOpenPrice,
              }),
              ...(update.cost !== undefined && { cost: update.cost }),
            };
            return {
              ...oldData,
              balances: updatedBalances,
            };
          } else {
            // Add new balance if not found (for different eventTokenIds queries)
            return {
              ...oldData,
              balances: [
                ...oldData.balances,
                {
                  id: update.assetId,
                  userId: null,
                  eventTokenId: update.assetId,
                  value: update.value,
                  totalVolume: update.totalVolume,
                  ...(update.averageOpenPrice !== undefined && {
                    averageOpenPrice: update.averageOpenPrice,
                  }),
                  ...(update.cost !== undefined && { cost: update.cost }),
                },
              ],
            };
          }
        });
      });
    });
  }, [queryClient]);

  // Debounced function to apply balance updates
  const debouncedUpdateBalances = useCallback(() => {
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }
    debounceTimeoutRef.current = setTimeout(() => {
      updateUserBalancesCache();
      debounceTimeoutRef.current = null;
    }, 150);
  }, [updateUserBalancesCache]);

  // Handle balance message - extracted to be reusable for mocking
  const handleBalanceMessage = useCallback(
    (balanceMsg: BalanceWebSocketMessage) => {
      // Handle balance updates - only show toast for BALANCE_UPDATE type
      if (balanceMsg.t === 'BALANCE_UPDATE' && balanceMsg.d && 'b' in balanceMsg.d) {
        const balanceData = balanceMsg.d as BalanceUpdateDto;

        const balance = balanceData.b;
        const frozen = balanceData.f ?? 0;
        const assetId = balanceData.a;

        if (balance !== undefined && balance !== null) {
          const balanceNum = typeof balance === 'string' ? parseFloat(balance) : balance;
          const frozenNum = typeof frozen === 'string' ? parseFloat(frozen) : frozen;

          if (
            !Number.isNaN(balanceNum) &&
            Number.isFinite(balanceNum) &&
            !Number.isNaN(frozenNum) &&
            Number.isFinite(frozenNum)
          ) {
            // Resolve coin info and previous balance before updating
            let resolvedCoinId = assetId;
            let coinLabel = '';
            // The toast renders at the coin's own range (dps..dpf), so a BTC balance
            // keeps its decimals (0.99999909) instead of collapsing to "1.00".
            let toastCoin: Coin | undefined;
            if (assetId) {
              const coin = coins.find((c) => c.id === assetId);
              toastCoin = coin;
              coinLabel = coin?.shortName?.toUpperCase() ?? '';
            } else {
              const usdcCoin = coins.find(
                (c: { shortName: string }) => c.shortName.toUpperCase() === 'USDC',
              );
              if (usdcCoin) {
                resolvedCoinId = usdcCoin.id;
                coinLabel = 'USDC';
                toastCoin = usdcCoin;
              }
            }

            const prevBalance = resolvedCoinId ? (getCoinBalance(resolvedCoinId)?.balance ?? 0) : 0;

            // Update coin balance (for coins, not user-balances)
            if (assetId) {
              setCoinBalance(assetId, balanceNum, frozenNum);
            } else if (resolvedCoinId) {
              setCoinBalance(resolvedCoinId, balanceNum, frozenNum);
            }

            // Update legacy balance in context for backward compatibility
            setBalance(balanceNum);

            // Handle user-balances update (for eventTokenIds)
            // b = totalVolume, f = frozen, value = b - f (available)
            // aop = averageOpenPrice (optional), c = cost (optional)
            if (assetId) {
              const availableValue = balanceNum - frozenNum;
              const timestamp = Date.now();

              // Parse optional fields from WebSocket
              const averageOpenPrice =
                balanceData.aop !== undefined
                  ? typeof balanceData.aop === 'string'
                    ? parseFloat(balanceData.aop)
                    : balanceData.aop
                  : undefined;
              const cost =
                balanceData.c !== undefined
                  ? typeof balanceData.c === 'string'
                    ? parseFloat(balanceData.c)
                    : balanceData.c
                  : undefined;

              // Validate optional fields
              const validAverageOpenPrice =
                averageOpenPrice !== undefined &&
                !Number.isNaN(averageOpenPrice) &&
                Number.isFinite(averageOpenPrice)
                  ? averageOpenPrice
                  : undefined;
              const validCost =
                cost !== undefined && !Number.isNaN(cost) && Number.isFinite(cost)
                  ? cost
                  : undefined;

              // Store update (will replace any existing update for this assetId)
              pendingUserBalanceUpdatesRef.current.set(assetId, {
                assetId,
                totalVolume: balanceNum,
                value: availableValue,
                ...(validAverageOpenPrice !== undefined && {
                  averageOpenPrice: validAverageOpenPrice,
                }),
                ...(validCost !== undefined && { cost: validCost }),
                timestamp,
              });

              // Notify balance subscribers (e.g. useDealsPositionsBalanceSync)
              notifyBalanceListeners({
                assetId,
                b: balanceNum,
                f: frozenNum,
                aop: validAverageOpenPrice ?? null,
                c: validCost ?? null,
              });

              // Debounce cache updates
              debouncedUpdateBalances();
            }

            // Show toast only for coin balances, not for user-balances.
            // Skip when delta is ~0 (e.g. success message already updated the
            // balance), and when a trade caused the change — the trade has its
            // own notification, so this generic one would be a duplicate.
            const tradeDriven = Date.now() - lastTradingTxAtRef.current < TRADING_BALANCE_MUTE_MS;
            if (coinLabel && !tradeDriven) {
              const delta = balanceNum - prevBalance;
              // Threshold scales with the coin's precision so small but meaningful
              // crypto changes (e.g. < 0.01 BTC) still notify.
              const epsilon = 10 ** -(toastCoin?.decimalPlacesShort ?? 3);
              if (Math.abs(delta) >= epsilon) {
                const sign = delta > 0 ? '+' : '';
                debouncedToast(
                  'success',
                  tToasts('balanceUpdated', {
                    coin: coinLabel === 'BONUS' ? tCommon('bonusAsset') : coinLabel,
                    amount: formatMoney(balanceNum, { coin: toastCoin }),
                    change: `${sign}${formatMoney(delta, { coin: toastCoin })}`,
                  }),
                );
              }
            }
          }
        }
      }
      // Handle success messages with balance list - update coin balances without toast
      else if (balanceMsg.t === 'success' && balanceMsg.d && 'l' in balanceMsg.d) {
        const successData = balanceMsg.d as SuccessMessageDto;
        if (successData.l && Array.isArray(successData.l)) {
          // Empty balance list means user has no coin balances — reset to zero
          if (successData.l.length === 0) {
            clearBalances();
            setBalance(0);
          } else {
            // Update balances for each coin in the list
            successData.l.forEach((item) => {
              const coinId = item.i;
              const balance = item.b;
              const frozen = item.f ?? 0;

              if (coinId && balance !== undefined && balance !== null) {
                const balanceNum = typeof balance === 'string' ? parseFloat(balance) : balance;
                const frozenNum = typeof frozen === 'string' ? parseFloat(frozen) : frozen;

                if (
                  !Number.isNaN(balanceNum) &&
                  Number.isFinite(balanceNum) &&
                  !Number.isNaN(frozenNum) &&
                  Number.isFinite(frozenNum)
                ) {
                  // Update coin balance
                  setCoinBalance(coinId, balanceNum, frozenNum);
                }
              }
            });

            // For backward compatibility, also update the old balance context with first item
            const firstItem = successData.l[0];
            const balance = firstItem.b;
            if (balance !== undefined && balance !== null) {
              const balanceNum = typeof balance === 'string' ? parseFloat(balance) : balance;
              if (!Number.isNaN(balanceNum) && Number.isFinite(balanceNum)) {
                // Update legacy balance in context without showing toast
                setBalance(balanceNum);
              }
            }
          }

          // ❌ Invalidation removed - centralized in OrderForm
          // queryClient.invalidateQueries({ queryKey: [USER_BALANCES_QK] });
        }
      }
    },
    [
      setBalance,
      setCoinBalance,
      getCoinBalance,
      clearBalances,
      coins,
      debouncedUpdateBalances,
      notifyBalanceListeners,
      debouncedToast,
      tToasts,
      tCommon,
    ],
  );

  // Cleanup debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    };
  }, []);

  // Handle authentication errors - logout if token is invalid
  const handleAuthError = useCallback(() => {
    if (!authErrorHandledRef.current) {
      authErrorHandledRef.current = true;
      clearAuthData();
    }
  }, [clearAuthData]);

  // Balance WebSocket
  const balanceSocket = useWebSocket({
    url: WS_ENDPOINTS.balance,
    autoConnect: isAuthenticated,
    reconnect: true,
    onOpen: () => {
      authErrorHandledRef.current = false; // Reset error flag on reconnect
    },
    onMessage: (msg) => {
      const balanceMsg = msg as unknown as BalanceWebSocketMessage;

      // Check for error in message (field 'r' or type 'error'/'ERROR')
      if (balanceMsg.r || balanceMsg.t === 'error' || balanceMsg.t === 'ERROR') {
        handleAuthError();
        return;
      }

      handleBalanceMessage(balanceMsg);
    },
    onErrorMessage: (_errorMsg) => {
      // Handle ERROR type messages - likely authentication failure
      handleAuthError();
    },
    onClose: (_event) => {
      authErrorHandledRef.current = false; // Reset error flag on close
    },
  });

  // Transaction WebSocket
  const transactionSocket = useWebSocket({
    url: WS_ENDPOINTS.transactions,
    autoConnect: isAuthenticated,
    reconnect: true,
    onOpen: () => {
      authErrorHandledRef.current = false; // Reset error flag on reconnect
    },
    onMessage: (msg) => {
      const transactionMsg = msg as unknown as TransactionWebSocketMessage;

      // Check for error in message (field 'r' or type 'error'/'ERROR')
      if (transactionMsg.r || transactionMsg.t === 'error' || transactionMsg.t === 'ERROR') {
        handleAuthError();
        return;
      }

      // Handle balance updates - BALANCE_UPDATE can come from transaction socket too
      if (transactionMsg.t === 'BALANCE_UPDATE') {
        handleBalanceMessage(transactionMsg as unknown as BalanceWebSocketMessage);
      }
      // Handle success messages with balance list - update balance without toast
      else if (transactionMsg.t === 'success' && transactionMsg.d && 'l' in transactionMsg.d) {
        handleBalanceMessage(transactionMsg as unknown as BalanceWebSocketMessage);
      }
      // Handle new transaction notifications - only show toast for TRANSACTION_UPDATE type
      else if (transactionMsg.t === 'TRANSACTION_UPDATE' && transactionMsg.d) {
        const transaction = transactionMsg.d as TransactionUpdateDto;

        // Handle error responses (transaction NOT created)
        if (transaction.er) {
          const errorMessages: Record<string, string> = {
            ERROR_BALANCE: tToasts('insufficientBalance'),
            ERROR_HOT_WALLET: tToasts('withdrawalsUnavailable'),
          };
          debouncedToast('error', errorMessages[transaction.er] ?? tToasts('withdrawalFailed'));
          return;
        }

        // Map WS DTO to domain model
        const mapped: Transaction = {
          id: transaction.i,
          userId: transaction.u,
          assetId: transaction.a,
          transactionType: transaction.t as Transaction['transactionType'],
          amount: transaction.am !== undefined ? Number(transaction.am) : null,
          status: transaction.s as Transaction['status'],
          createdAt: transaction.cat,
          externalTransactionId: transaction.e,
          fees: transaction.f !== undefined ? Number(transaction.f) : null,
          outcomeName: transaction.on,
          tokenName: transaction.tn,
          isPositive: transaction.ip,
          eventSlug: transaction.es,
          toAddress: transaction.ta,
          blockExplorerUrl: transaction.bu,
          blockchainName: transaction.bn,
        };

        // WS payloads omit REST-only fields (bn/bu, …) — merge into the cached
        // entry instead of replacing it, so those values survive status updates.
        const mergeTx = (existing: Transaction): Transaction => {
          const patch = Object.fromEntries(
            Object.entries(mapped).filter(([, v]) => v != null),
          ) as Partial<Transaction>;
          return { ...existing, ...patch };
        };

        // Update all transaction caches directly instead of invalidating
        const allTxQueries = queryClient.getQueryCache().findAll({
          queryKey: [TRANSACTIONS_QK],
        });

        allTxQueries.forEach((query) => {
          const data = query.state.data;
          if (!data) return;

          // Check if this query filters by statuses or types
          // Query key shape: [TRANSACTIONS_QK, 'infinite', params] or [TRANSACTIONS_QK, params]
          const queryParams = query.queryKey.find((k) => typeof k === 'object' && k !== null) as
            | { statuses?: string; types?: string }
            | undefined;
          const allowedStatuses = queryParams?.statuses?.split(',') ?? [];
          const statusMismatch =
            allowedStatuses.length > 0 && !allowedStatuses.includes(mapped.status);
          const allowedTypes = queryParams?.types?.split(',') ?? [];
          const typeMismatch =
            allowedTypes.length > 0 && !allowedTypes.includes(mapped.transactionType);

          // Handle InfiniteData (useInfiniteTransactions)
          if ('pages' in (data as Record<string, unknown>)) {
            queryClient.setQueryData<InfiniteData<TransactionsResult>>(query.queryKey, (old) => {
              if (!old) return old;

              // If status or type doesn't match query filter, remove from cache
              if (statusMismatch || typeMismatch) {
                const filtered = old.pages.map((page) => ({
                  ...page,
                  transactions: page.transactions.filter((t) => t.id !== mapped.id),
                }));
                return { ...old, pages: filtered };
              }

              let found = false;
              const updatedPages = old.pages.map((page) => {
                const idx = page.transactions.findIndex((t) => t.id === mapped.id);
                if (idx >= 0) {
                  found = true;
                  const updated = [...page.transactions];
                  updated[idx] = mergeTx(updated[idx]);
                  return { ...page, transactions: updated };
                }
                return page;
              });
              // If not found in any page, prepend to first page (new transaction)
              if (!found && updatedPages.length > 0) {
                const firstPage = updatedPages[0];
                updatedPages[0] = {
                  ...firstPage,
                  transactions: [mapped, ...firstPage.transactions],
                };
              }
              return { ...old, pages: updatedPages };
            });
          }
          // Handle regular TransactionsResult (useTransactions)
          else if ('transactions' in (data as Record<string, unknown>)) {
            queryClient.setQueryData<TransactionsResult>(query.queryKey, (old) => {
              if (!old) return old;

              // If status or type doesn't match query filter, remove from cache
              if (statusMismatch || typeMismatch) {
                return {
                  ...old,
                  transactions: old.transactions.filter((t) => t.id !== mapped.id),
                };
              }

              const idx = old.transactions.findIndex((t) => t.id === mapped.id);
              if (idx >= 0) {
                const updated = [...old.transactions];
                updated[idx] = mergeTx(updated[idx]);
                return { ...old, transactions: updated };
              }
              // Prepend new transaction
              return { ...old, transactions: [mapped, ...old.transactions] };
            });
          }
        });
        // Notify subscribers
        notifyTxListeners(mapped);

        // Show toast based on transaction type + status
        const txType = (transaction.t ?? '').toUpperCase();
        const status = (transaction.s ?? '').toUpperCase();
        // A first deposit closes an onboarding step on the backend, a little
        // later; the hub's counter re-reads once it has.
        if (txType === 'DEPOSIT' && status === 'COMPLETED') {
          scheduleRewardsCountersRefresh(queryClient);
        }
        const txCoin = coins.find((c) => c.id === mapped.assetId);
        const txCoinLabel = txCoin?.shortName?.toUpperCase() ?? '';
        const amountRaw = transaction.am;
        const amountNum =
          amountRaw !== undefined
            ? typeof amountRaw === 'string'
              ? parseFloat(amountRaw)
              : amountRaw
            : undefined;
        // Coin's own FULL precision — token rewards can be fractions like 0.00043
        // that the coin's `dps` (2 for OPL/BONUS) would collapse into "> 0.01".
        const amountStr =
          amountNum !== undefined && !Number.isNaN(amountNum)
            ? formatMoney(Math.abs(amountNum), { coin: txCoin })
            : '';

        // Ledger entries produced by a trade carry no toast of their own. Futures
        // trades are announced by `TRADE_TOAST`, so they also mute the generic
        // `balanceUpdated`; prediction trades keep it as their balance notice.
        if (TRADING_TX_TYPES.has(txType)) {
          if (txType.startsWith('FUT_')) {
            lastTradingTxAtRef.current = Date.now();
            // The balance frame may have landed first and already scheduled one.
            debouncedToast.cancel();
          }
          return;
        }

        // Transaction types that should not trigger toasts.
        // PROMO_GRANT is announced by the promo claim flow itself ("+$10 credited to your
        // Wallet") — the generic fallback toast below would double it. PROMO_BURN and
        // BONUS_CONSUME are internal ledger side-effects of an action (withdrawal,
        // opening a Boost-margined position) that already has its own toast/notice.
        const SILENT_TX_TYPES = new Set([
          'FEE',
          'SYSTEM',
          'PROMO_GRANT',
          'PROMO_BURN',
          'BONUS_CONSUME',
        ]);
        if (SILENT_TX_TYPES.has(txType)) {
          return;
        }

        // Funding is the one futures ledger entry with no trade behind it —
        // internal entry, no status variants; shown as a neutral notice.
        const FUTURES_TX_KEYS: Record<string, string> = {
          FUT_FUNDING: 'futFunding',
        };
        const futuresKey = FUTURES_TX_KEYS[txType];
        if (futuresKey) {
          if (amountStr) {
            debouncedToast(
              'info',
              tToasts(futuresKey as Parameters<typeof tToasts>[0], {
                amount: amountStr,
                coin: txCoinLabel,
              }),
            );
          }
          return;
        }

        // Reward transactions only surface a toast on COMPLETED — intermediate
        // statuses (CREATED/PENDING/etc.) are implementation details the user
        // doesn't need to see, and translations only exist for *Completed.
        const REWARD_TX_TYPES = new Set(['TOKEN_REWARD', 'REFERRAL_TOKEN_REWARD', 'REF']);
        if (REWARD_TX_TYPES.has(txType) && status !== 'COMPLETED') {
          return;
        }

        // Build toast key: e.g. "depositCompleted", "withdrawalCreated"
        const typeKeyMap: Record<string, string> = {
          DEPOSIT: 'deposit',
          WITHDRAWAL: 'withdrawal',
          TOKEN_REWARD: 'tokenReward',
          REFERRAL_TOKEN_REWARD: 'referralTokenReward',
          REF: 'referral',
        };
        const statusKeyMap: Record<string, string> = {
          CREATED: 'Created',
          PENDING: 'Pending',
          AWAITING: 'Awaiting',
          COMPLETED: 'Completed',
          ERROR: 'Error',
          CANCELED_BY_USER: 'Canceled',
          CANCELED_BY_MANAGER: 'Canceled',
          CANCELED_BY_SYSTEM: 'Canceled',
          UNDER_REVIEW: 'UnderReview',
          CANCELLED: 'Canceled',
          REJECTED: 'Rejected',
        };

        const typeKey = typeKeyMap[txType];
        const statusKey = statusKeyMap[status];

        const NEGATIVE_STATUSES = new Set([
          'CANCELED_BY_MANAGER',
          'CANCELED_BY_SYSTEM',
          'CANCELLED',
          'REJECTED',
          'ERROR',
        ]);

        if (typeKey && statusKey) {
          const toastKey = `${typeKey}${statusKey}` as Parameters<typeof tToasts>[0];
          const toastLevel = NEGATIVE_STATUSES.has(status) ? 'error' : 'success';
          debouncedToast(toastLevel, tToasts(toastKey, { amount: amountStr, coin: txCoinLabel }));
        } else if (amountStr) {
          // Fallback for other transaction types
          debouncedToast(
            'success',
            tToasts('transactionUpdate', {
              type: txType.toLowerCase(),
              amount: amountStr,
              coin: txCoinLabel,
            }),
          );
        } else {
          debouncedToast('success', tToasts('transactionReceived'));
        }
      }
      // Handle hot wallet status updates — patch blockchain status in cache
      else if (
        transactionMsg.t === 'HOT_WALLET_STATUS_UPDATE' &&
        transactionMsg.d &&
        'bi' in transactionMsg.d
      ) {
        const { bi, ws } = transactionMsg.d as HotWalletStatusUpdateDto;
        queryClient.setQueriesData<Blockchain[]>({ queryKey: [BLOCKCHAINS_QK] }, (old) => {
          if (!old) return old;
          return old.map((chain) => (chain.id === bi ? { ...chain, status: ws } : chain));
        });
      }
    },
    onErrorMessage: (_errorMsg) => {
      // Handle ERROR type messages - likely authentication failure
      handleAuthError();
    },
    onClose: (_event) => {
      authErrorHandledRef.current = false; // Reset error flag on close
    },
  });

  // Reconnect sockets when auth state changes; clear stale data on logout
  useEffect(() => {
    // Auth is still being resolved — don't touch sockets or the cache yet.
    // Acting on the initial unauthenticated pass would wipe the cache and force
    // every in-flight public query (geolocation / lastDay / recent) to refetch.
    if (isAuthLoading) return;

    if (isAuthenticated) {
      wasAuthenticatedRef.current = true;
      authErrorHandledRef.current = false; // Reset error flag when reconnecting
      balanceSocket.connect();
      transactionSocket.connect();
    } else {
      balanceSocket.disconnect();
      transactionSocket.disconnect();

      // Clear all in-memory balance state so a subsequent login
      // never briefly shows the previous user's data
      clearBalances();
      setBalance(0);
      pendingUserBalanceUpdatesRef.current.clear();
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
        debounceTimeoutRef.current = null;
      }

      // Remove ALL cached queries to prevent data leaking between user sessions —
      // but ONLY on a genuine logout (was authenticated, now not), never on the
      // first anonymous load, which would otherwise double-fetch public queries.
      if (wasAuthenticatedRef.current) {
        wasAuthenticatedRef.current = false;
        queryClient.clear();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthLoading,
    isAuthenticated,
    transactionSocket.disconnect,
    setBalance,
    balanceSocket.disconnect,
    transactionSocket.connect, // Remove ALL cached queries to prevent data leaking between user sessions.
    // This is safer than maintaining a list of individual query keys.
    queryClient.clear, // Clear all in-memory balance state so a subsequent login
    // never briefly shows the previous user's data
    clearBalances,
    balanceSocket.connect,
  ]);

  const txUpdateCtx = useMemo(() => ({ subscribe: subscribeTxUpdates }), [subscribeTxUpdates]);
  const balanceUpdateCtx = useMemo(
    () => ({ subscribe: subscribeBalanceUpdates }),
    [subscribeBalanceUpdates],
  );

  return (
    <TransactionUpdateContext.Provider value={txUpdateCtx}>
      <BalanceUpdateContext.Provider value={balanceUpdateCtx}>
        {children}
      </BalanceUpdateContext.Provider>
    </TransactionUpdateContext.Provider>
  );
}
